/**
 * Robot receipts: has THIS PAGE seen this robot's record advance, and
 * how long ago (twin slice 1, plan W3 step 5)?
 *
 * WHY NOT `Date.now() - updated_at_ms`. That stamp is the worker's
 * clock. Any skew between the worker and this device makes a dead feed
 * look live or a live one dead, and the worker re-stamps a silent
 * robot's stub every second, so the stamp advancing proves only that
 * the WORKER is alive. The age that means "reporting now" is measured
 * here, on this device's monotonic clock, from this page's own receipt
 * of a record whose stamp advanced.
 *
 * THE TWO-RECEIPT RULE (catalogue §2). A record seen once is "checking":
 * a REST seed, an SSE replay of a dead worker's retained row, or the
 * first record after a reconnect all look exactly like a live one.
 * Only a SECOND, strictly newer record shows the feed is moving. The
 * REST seed never passes through here at all, so it can never count.
 *
 * WHERE `note()` RUNS. On the raw SSE event, not on `useLiveData`'s
 * 500 ms display flush: the flush sees only the newest record per robot
 * per tick, so two receipts inside one tick would count as one, and
 * every receipt would be stamped up to half a second late.
 *
 * RESETS. Counts and stamps are forgotten when the stream (re)opens and
 * when the page wakes (`useLiveData`), so every robot goes back to
 * "checking" together. Two things are NOT forgotten:
 *
 *   - WHEN each robot was last heard. A reset says the next record could
 *     be a replay; it does not say the robot reported any more recently
 *     than it did. Forgetting the time made every robot younger on each
 *     reset, and a stream that opens and then carries nothing (the
 *     platform wedge the read-idle watchdog trips on, ~15.5 s a cycle)
 *     reopened often enough to hold a robot silent for minutes at
 *     "checking", a dimmed mark at its stale pose that never became a
 *     ghost. So the age runs from the last counted receipt whatever the
 *     stream does, and only a NEW watch of the site (`beginWatch`)
 *     restarts the clock for a robot never heard from.
 *   - The last REAL pose each robot reported: that is the session's
 *     ghost, the place a robot that goes silent is drawn as last seen,
 *     and a reset says nothing about where it was.
 */
import { useSyncExternalStore } from 'react';
import { QUIET_AFTER_MS } from '../state/liveness';
import { monotonicNow } from '../state/clock';

/** A robot's receipt key: fleet-qualified, as locate links spell it. */
export function robotReceiptKey(fleet: string, robot: string): string {
  return `${fleet}/${robot}`;
}

/** A pose a robot really reported: a level, and not the LOST sentinel. */
export interface RealPose {
  readonly map: string;
  readonly x: number;
  readonly y: number;
  readonly yaw: number | null;
  /** The worker stamp of the record it came from (worker clock). */
  readonly atMs: number;
}

export interface ReceiptView {
  /** Strictly newer records received since the last reset. */
  readonly count: number;
  /** `monotonicNow()` at the last counted receipt, kept across resets;
   *  null when this robot has never been heard. */
  readonly lastAt: number | null;
}

interface Entry {
  count: number;
  lastAt: number | null;
  lastStamp: number | null;
}

/**
 * A real pose out of a location, or null for the two shapes that are
 * not one: the worker's stub (no level) and the adapter's LOST sentinel
 * (exactly the origin, which no waypoint at this site is near). The
 * same exclusions `pose_recorder.pose_from_payload` applies server side.
 */
export function realPoseOf(
  location: { map?: unknown; x?: unknown; y?: unknown; yaw?: unknown } | null | undefined,
  atMs: number,
): RealPose | null {
  if (!location || typeof location.map !== 'string' || location.map === '') return null;
  const { x, y, yaw } = location;
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x === 0 && y === 0) return null;
  return {
    map: location.map,
    x,
    y,
    yaw: typeof yaw === 'number' && Number.isFinite(yaw) ? yaw : null,
    atMs,
  };
}

export class RobotReceipts {
  private readonly _clock: () => number;
  private readonly _entries = new Map<string, Entry>();
  private readonly _poses = new Map<string, RealPose>();
  private readonly _listeners = new Set<() => void>();
  private _watchingSince: number | null = null;
  private _version = 0;

  constructor(clock: () => number = monotonicNow) {
    this._clock = clock;
  }

  /**
   * One raw robot record off the stream (a v1 `RobotState` payload).
   * Anything without a fleet, a robot name and a numeric stamp is not a
   * record this can judge, and is ignored. Returns whether it counted.
   */
  noteRobot(payload: unknown): boolean {
    if (!payload || typeof payload !== 'object') return false;
    const p = payload as {
      id?: { fleet?: unknown; robot?: unknown };
      updated_at_ms?: unknown;
      unavailable_reason?: unknown;
      location?: { map?: unknown; x?: unknown; y?: unknown; yaw?: unknown } | null;
    };
    const fleet = p.id?.fleet;
    const robot = p.id?.robot;
    const stamp = p.updated_at_ms;
    if (typeof fleet !== 'string' || typeof robot !== 'string' || robot === '') return false;
    if (typeof stamp !== 'number' || !Number.isFinite(stamp)) return false;
    // A record that names a reason is not a pose the robot reported,
    // whatever its location says (the reason and the stub shape do not
    // always travel together).
    const reason = typeof p.unavailable_reason === 'string' && p.unavailable_reason !== '';
    return this.note(robotReceiptKey(fleet, robot), stamp, reason ? null : realPoseOf(p.location, stamp));
  }

  /**
   * Count a receipt for `key` when `stampMs` is STRICTLY newer than the
   * last stamp counted since the reset. An equal stamp is the same
   * record delivered again; an older one arrived out of order. Neither
   * shows the feed moving.
   */
  note(key: string, stampMs: number, pose: RealPose | null = null): boolean {
    if (pose) {
      const prev = this._poses.get(key);
      if (!prev || pose.atMs >= prev.atMs) this._poses.set(key, pose);
    }
    const now = this._clock();
    const e = this._entries.get(key) ?? { count: 0, lastAt: null, lastStamp: null };
    if (e.lastStamp !== null && !(stampMs > e.lastStamp)) return false;
    // Salient only when the reading it gives can change: the first and
    // second receipts (checking → live), or a receipt after the record
    // had gone quiet. A robot reporting steadily every second changes
    // nothing here and must not re-render anything.
    const salient = e.count < 2 || e.lastAt === null || now - e.lastAt >= QUIET_AFTER_MS;
    e.count += 1;
    e.lastAt = now;
    e.lastStamp = stampMs;
    this._entries.set(key, e);
    if (salient) this._bump();
    return true;
  }

  /**
   * Forget every count and stamp (stream opened, page woke), so the next
   * record of each robot counts as its first. Keeps when each robot was
   * last heard, and the last real poses: a reset must never make a
   * robot look more recently heard than it was. Starts the "watching
   * since" clock only if no watch has begun.
   */
  reset(): void {
    for (const e of this._entries.values()) {
      e.count = 0;
      e.lastStamp = null;
    }
    if (this._watchingSince === null) this._watchingSince = this._clock();
    this._bump();
  }

  /**
   * A new watch of this site (`useLiveData` mounting for it): a reset,
   * and the "watching since" clock restarts, so a robot never heard from
   * is measured from now. Only this moves that clock; a reopened stream
   * or a wake is the SAME watch, and restarting it there would keep a
   * never-heard robot at "checking" for as long as the stream cycles.
   */
  beginWatch(): void {
    this._watchingSince = this._clock();
    this.reset();
  }

  get(key: string): ReceiptView {
    const e = this._entries.get(key);
    return { count: e?.count ?? 0, lastAt: e?.lastAt ?? null };
  }

  /**
   * How long this robot has been without a receipt, on the monotonic
   * clock: since its last counted receipt (resets do not move it), or --
   * when it has never been heard -- since the watch began. Null when
   * nothing is being watched at all.
   */
  ageMs(key: string, now: number = this._clock()): number | null {
    const e = this._entries.get(key);
    const since = e?.lastAt ?? this._watchingSince;
    return since === null ? null : now - since;
  }

  /** The newest real pose this page has seen for `key`, ever. */
  lastRealPose(key: string): RealPose | undefined {
    return this._poses.get(key);
  }

  watchingSince(): number | null {
    return this._watchingSince;
  }

  now(): number {
    return this._clock();
  }

  /** Changes only on a salient receipt or a reset. */
  version(): number {
    return this._version;
  }

  subscribe(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private _bump(): void {
    this._version += 1;
    for (const l of this._listeners) l();
  }
}

const _bySite = new Map<string, RobotReceipts>();

/** The page's receipt book for `site`, created on first use. */
export function robotReceiptsFor(site: string): RobotReceipts {
  let book = _bySite.get(site);
  if (!book) {
    book = new RobotReceipts();
    _bySite.set(site, book);
  }
  return book;
}

/** Tests only: forget every site's book. */
export function _resetRobotReceiptsForTests(): void {
  _bySite.clear();
}

/**
 * The receipt book for `site`, re-rendering on a salient change (a
 * robot's first or second receipt, a recovery from quiet, a reset).
 * Ages between those changes are the boundary timer's job
 * (`useBoundaryTick`), not this hook's. Returns the version alongside
 * the book so it can key a memo.
 */
export function useRobotReceipts(site: string): { book: RobotReceipts; version: number } {
  const book = robotReceiptsFor(site);
  const version = useSyncExternalStore(
    (l) => book.subscribe(l),
    () => book.version(),
    () => book.version(),
  );
  return { book, version };
}
